import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react"

import { rootStore, useStores } from "stores/root-store"

import AlertDialog from "components/common/alert-dialog"
import { LoggingHelper } from "utils/logging-helper"
import { FormWrapper } from "components/common-styles"
import InboundDeliveryInfo from "./inbound-delivery-info"
import InboundDeliveryProduct from "./inbound-delivery-product"

import InboundDeliveryPaperwork from "./inbound-delivery-paperwork"
import { ArrowBack } from "@mui/icons-material"
import { SailecBoldFont } from "./sailec-bold-font"
import { SailecRegularFont } from "./sailec-regular-font"
import { LogoData } from "./logo-data"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import { formatDisplayDate } from "utils/date-helper"
import { errorType, trackEvent } from "utils/analytics"
import CommonFormActions from "components/common/common-form-actions"
import { addDays } from "date-fns"

const InboundDeliveryForm = ({ onlyShowPaperWork, onClose, onSave }) => {
  const { InboundDeliveryStore } = useStores()
  const initialValues = useMemo(() => {
    return {
      receipt_id: "",
      erp_order: "",
      trailer_id: "",
      source: "",
      warehouse: "",
      receipt_type: "",
      scheduled_date: addDays(new Date(), 1),
    }
  }, [])

  const [openPaperwork, setOpenPaperwork] = useState(false)
  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")

  const formRef = useRef()
  const productsRef = useRef()
  const paperWorkRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deliveryInfo, setDeliveryInfo] = useState(initialValues)
  const [products, setProducts] = useState([])
  const [wasSuccess, setWasSuccess] = useState(true)
  const [erpOrder, setErpOrder] = useState("")

  const initData = useCallback(() => {
    if (InboundDeliveryStore.deliveryInfo) {
      const data = { ...InboundDeliveryStore.deliveryInfo }
      data.scheduled_date = new Date(data.scheduled_date)
      setProducts(JSON.parse(JSON.stringify(InboundDeliveryStore.products || [])))
      data.oldProducts = JSON.parse(JSON.stringify(InboundDeliveryStore.products || []))
      setDeliveryInfo(data)
      setErpOrder(data.erp_order)
    } else {
      setDeliveryInfo(initialValues)
    }
  }, [InboundDeliveryStore.deliveryInfo, InboundDeliveryStore.products, initialValues])

  useEffect(() => {
    initData()

    setOpenPaperwork(onlyShowPaperWork || false)
  }, [initData])

  const handleSubmit = async () => {
    const resInfo = await formRef.current.getFormDataIfValid()
    if (!resInfo) return

    const resProducts = await productsRef.current.getProductsIfValid()
    if (!resProducts) return

    const newInfo = { ...deliveryInfo, ...resInfo, products: resProducts }

    setIsSubmitting(true)
    setOpenAlert(true)
    const result = await InboundDeliveryStore.addOrUpdateData(newInfo)
    LoggingHelper.writeLog("InboundDeliveryForm", "submit", `${result ? JSON.stringify(result) : ""}`)
    setIsSubmitting(false)

    setWasSuccess(true)
    let msg = InboundDeliveryStore.deliveryInfo ? "Data Updated Successfully" : "New Data Added Successfully"
    const acceptedTransactions = result?.acceptedTransactions || result?.resource?.acceptedTransactions || result?.error?.context?.acceptedTransactions || 0
    if (result && (result.resource?.message || result.error?.message || result.message || typeof result == "string") && acceptedTransactions < 1) {
      setWasSuccess(false)
      msg = result.resource?.message || result.error?.message || result.message || result
      trackEvent("record_save", { entity: "inbound_delivery", mode: InboundDeliveryStore.deliveryInfo ? "edit" : "add", result: "failed", error_type: errorType(result) })
    } else {
      trackEvent("record_save", { entity: "inbound_delivery", mode: InboundDeliveryStore.deliveryInfo ? "edit" : "add", result: "success" })
    }
    setAlertContent(msg)
    setDeliveryInfo(newInfo)
  }

  const handleClose = () => {
    initData()

    if (openPaperwork && !onlyShowPaperWork) onSave()
    else onClose()
  }

  const onOK = () => {
    setOpenAlert(false)
    if (wasSuccess) {
      setOpenPaperwork(true)
    }
  }

  const printDocument = async () => {
    const { location, packagingType, quantity, despatchDate, deliveryDate } = await paperWorkRef.current.getFormDataIfValid()
    const input = document.getElementById("divToPrint")

    const count = quantity ? quantity : 1

    let supplierName = deliveryInfo.source
    if (supplierName === null || supplierName === "") {
      supplierName = "-"
    }
    const receipt_id = deliveryInfo.receipt_id
    const company = deliveryInfo.company || rootStore.company
    html2canvas(input, { scale: 2 }).then(canvas => {
      const pdf = new jsPDF("l", "mm", "a4")
      for (let i = 0; i < count; i++) {
        pdf.addFileToVFS("Sailec_bold.ttf", SailecBoldFont)
        pdf.addFileToVFS("Sailec_regular.ttf", SailecRegularFont)
        pdf.addImage(LogoData, "JPEG", 15, 15, 70, 40)
        pdf.setLineWidth(0.5)
        pdf.line(90, 10, 90, 60)
        pdf.setFontSize(32)

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.text(100, 25, receipt_id)

        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.setFontSize(18)
        pdf.text(100, 40, "C/- eStore Logistics")
        pdf.text(location, 100, 50)

        pdf.addFont("Sailec", "Sailec", "normal")
        pdf.setLineWidth(0.5)
        pdf.line(10, 60, 285, 60)

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.setFontSize(28)
        pdf.text(20, 75, "Supplier Name:")
        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.text(105, 75, `${supplierName}`)

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.text(20, 100, "Company Code:")
        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.text(105, 100, company?.toUpperCase())

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.text(20, 125, "Packaging Type:")
        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.text(105, 125, `${packagingType}`)

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.text(20, 150, "Qty:")
        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.text(105, 150, (i + 1).toString() + " of " + count.toString())
        pdf.setLineWidth(0.5)
        pdf.line(10, 165, 285, 165)
        pdf.setFontSize(18)

        pdf.getStyle("bold")

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.text(20, 175, "Despatch Date")
        pdf.addFont("normal")
        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.text(20, 185, `${formatDisplayDate(despatchDate)}`)

        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.text(200, 175, "Expected Delivery Date")
        pdf.addFont("normal")
        pdf.addFont("Sailec_Regular.ttf", "regular", "normal")
        pdf.setFont("regular", "normal", "normal")
        pdf.text(210, 185, `${formatDisplayDate(deliveryDate)}`)

        //rectangle
        pdf.rect(10, 10, 275, 180)
        pdf.addFont("Sailec_bold.ttf", "Sailec-Bold", "normal")
        pdf.setFont("Sailec-Bold", "normal", "normal")
        pdf.setFontSize(14)
        pdf.text(75, 202, "* Please note - print and attach label to each pallet/carton for delivery")

        if (i < quantity - 1) {
          pdf.addPage()
        }
      }
      pdf.save("download.pdf")
      trackEvent("export", { entity: "inbound_delivery_paperwork", format: "pdf" })

      if (!onlyShowPaperWork) onSave()
      else onClose()
    })
  }

  return (
    <>
      <FormWrapper spacing={2}>
        <Stack direction={"row"} spacing={2}>
          <Tooltip placement="top" title="Back">
            <IconButton onClick={handleClose}>
              <ArrowBack />
            </IconButton>
          </Tooltip>

          <Box>
            {InboundDeliveryStore.deliveryInfo ? (
              <>
                <Typography variant="h5">Edit Inbound Delivery: {InboundDeliveryStore.deliveryInfo.receipt_id}</Typography>
                <small>Review delivery and product details on an existing inbound delivery</small>
              </>
            ) : (
              <>
                <Typography variant="h5">New Inbound Delivery</Typography>
                <small>Enter delivery and product details to create a new inbound delivery</small>
              </>
            )}
          </Box>
        </Stack>

        {openPaperwork ? (
          <InboundDeliveryPaperwork deliveryInfo={deliveryInfo} ref={paperWorkRef} />
        ) : (
          <>
            <InboundDeliveryInfo deliveryInfo={deliveryInfo} ref={formRef} onErpOrderChange={setErpOrder} />
            <InboundDeliveryProduct erp_order={erpOrder} curProducts={products} ref={productsRef} isSubmitting={isSubmitting} />
          </>
        )}

        <CommonFormActions openPaperwork={openPaperwork} printDocument={printDocument} isSubmitting={isSubmitting} handleSubmit={handleSubmit} handleClose={handleClose} />
      </FormWrapper>
      <AlertDialog isProgress={isSubmitting} content={alertContent} open={openAlert} onClose={() => setOpenAlert(false)} onOK={onOK} />
    </>
  )
}

export default memo(InboundDeliveryForm)
